import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import { TENANT_STATUSES } from "./TenantStatusPill";

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Operator-only form to create a new tenant company.
 * Posts to ``POST /api/platform/companies`` and routes to the
 * newly-created tenant's detail page on success.
 */
export default function CreateTenantForm() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [status, setStatus] = useState("trial");
  const [submitting, setSubmitting] = useState(false);

  const onNameChange = (v) => {
    setName(v);
    if (!slugTouched) {
      setSlug(slugify(v));
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Tenant name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        slug: (slug || slugify(trimmedName)).trim().toLowerCase(),
        status,
      };
      const { data } = await api.post("/platform/companies", payload);
      toast.success(`Tenant "${data.company.name}" created`);
      nav(`/platform/companies/${data.company.id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="platform-create-tenant-page" className="max-w-2xl">
      <div className="mb-4">
        <Link
          to="/platform"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink transition-colors"
          data-testid="create-tenant-back"
        >
          <ArrowLeft size={14} />
          Back to tenants
        </Link>
      </div>

      <PageHeader
        eyebrow="Control Center"
        title="New tenant"
        subtitle="Provision a new customer company. After creation you'll be able to add stores, departments, and the first admin user."
      />

      <Card variant="raised" className="p-6">
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="data-sku block mb-1">Company name *</label>
            <Input
              placeholder="e.g. Cheese & Wine Co."
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              autoFocus
              data-testid="create-tenant-name"
              leadingIcon={<Building2 size={16} />}
            />
          </div>

          <div>
            <label className="data-sku block mb-1">URL slug *</label>
            <Input
              placeholder="e.g. cheese-wine-co"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              className="font-mono"
              data-testid="create-tenant-slug"
            />
            <p className="font-mono text-[11px] text-muted-foreground mt-1">
              Auto-generated from the name. Must be unique across all tenants.
            </p>
          </div>

          <div>
            <label className="data-sku block mb-1">Initial status</label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="create-tenant-status"
            >
              {TENANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            <p className="font-mono text-[11px] text-muted-foreground mt-1">
              {`"trial" or "active" allow login; "suspended", "cancelled", and "archived" block tenant logins immediately.`}
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              tier="primary"
              type="submit"
              disabled={submitting || !name.trim() || !slug.trim()}
              data-testid="create-tenant-submit"
            >
              {submitting ? "Creating…" : "Create tenant"}
            </Button>
            <Button
              tier="tertiary"
              type="button"
              onClick={() => nav("/platform")}
              disabled={submitting}
              data-testid="create-tenant-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
